<template lang="pug">
  .presentation.column
    .resource.row
      .item-1.author-tooltip-container.column.start
        router-link.row.middle.center.m-top-2.m-bottom-1(:to='{name: routes.kreaResourceTeacher}')
          p.dark-gray.text-normal.bold.m-right-1 {{ innerI18n.t('presentationTab.explore') }}
          simple-svg.dark-gray-icon.m4-top-1(:filepath='arrowRight' :width='"20px"' :height='"20px"')
        .info.column.start.top.m-bottom-6
          krea-text-area.m-bottom-2.width-100#rhetoricalQuestion(
            edit
            name='rhetoricalQuestion'
            :info='rhetoricalQuestionInfo'
            :value='activeLocalizedResource.rhetoricalQuestion'
            :updateMethod='editLocalizedField'
            :maxCharacters='300'
            :placeholder='innerI18n.t("krea.resource.presentationTab.rhetoricalQuestionPlaceholder")'
            size='title-medium'
            height='150px'
          )
          .column.top.start.m-bottom-4.width-100#benefits
            h2.m-bottom-1.title-small {{ innerI18n.t('presentationTab.benefits') }}
            krea-text-area.m-bottom-2.width-100(
              edit
              name='benefits'
              :info='benefitsInfo'
              :value='activeLocalizedResource.benefits'
              :updateMethod='editLocalizedField'
              :placeholder='innerI18n.t("krea.resource.presentationTab.benefitsPlaceholder")'
              size='normal'
              :maxCharacters='350'
              height='150px'
            )
        author-bio-tooltip.bottom(v-if='resource.mainAuthor' :author='resource.mainAuthor' styleClass='author-bio-popover program-author-popover')
          .author.row
            img.author-avatar(:src='resource.mainAuthor.picture || defaultImage' :alt='`${resource.mainAuthor.name} avatar`')
            span.text-big.black.m-bottom-1 {{ resource.mainAuthor.name }}
            span.text-normal.dark-gray {{ resource.mainAuthor.bio }}
      .item-1.column.start.top
        v-popover.width-100(
          placement='top-end'
          :delay='{show: 100, hide: 600}'
          trigger='hover click'
          popoverClass='krea-actions-popover'
          :container='false'
          :disabled='!activeLocalizedResource.mediaPresentation'
        )
          .width-100
            .media-box.column.middle.center(v-if='activeLocalizedResource.mediaPresentation')
              img.media-picture(:src='activeLocalizedResource.mediaPresentation' v-if='mediaIsNotVideo')
              .video.width-100(v-else v-html='mediaPresentationHtml')
            .media-box.empty.column.middle.center(v-else)
              krea-file-uploader.media-box-uploader(
                :navigationLanguage='false'
                link type='image' linkType='video'
                :updateFile='editResourcePresentationImage'
                :updateLink='editResourcePresentationVideo'
              )
          template(slot='popover')
            actions(
              file fileLink
              fileType='image'
              placement='end'
              :info='mediaPresentationInfo'
              :fileValue='activeLocalizedResource.mediaPresentation'
              @removeFile='editResourcePresentationImage(null)'
              :updateFile='editResourcePresentationImage'
            )
        .materials-items.row.space-between(v-if='hasMaterials')
          p.text-small.dark-gray {{ innerI18n.t('presentationTab.need') }}
          .row.middle(v-show='hasElectronicMaterials')
            simple-svg.small-icon.m-right-05(:filepath='electronicMaterialIcon' width='18px' height='18px' )
            p.text-normal {{ innerI18n.t('presentationTab.electronicMaterial') }}
          .row.middle(v-show='hasPhysicalMaterials')
            simple-svg.small-icon.m-right-05(:filepath='physicalMaterialIcon' width='18px' height='18px' )
            p.text-normal {{ innerI18n.t('presentationTab.classMaterial') }}
          .row.middle(v-show='hasDownloadableMaterials')
            simple-svg.small-icon.m-right-05(:filepath='downloadableMaterialIcon' width='17px' height='17px' )
            p.text-normal {{ innerI18n.t('presentationTab.downloadMaterial') }}
        .graphics-items
          .row.item
            .row.top.start.m-bottom-1
              img.item-logo(src='@/assets/ic_user.svg' width='19px' alt='resource age logo')
            .item-info.column.top.start
              h3.m-bottom-05.text-small.dark-gray {{ innerI18n.t('krea.educationalContent.features.ageRangeLabel') }}
              p.row.middle.text-normal.age-desc(v-if='resourceAge' :style='getAgeRangeStyle(resourceAge.mainColor)')
                | {{ resourceAge.minAge }} {{ innerI18n.t('teacher.ageSeparator')}} {{ resourceAge.maxAge }} {{ innerI18n.t('teacher.years')}}
              p.row.middle.text-normal.age-desc.m-top-1(v-if='resourceSecondaryAge' :style='getAgeRangeStyle(resourceSecondaryAge.mainColor)')
                | {{ resourceSecondaryAge.minAge }} {{ innerI18n.t('teacher.ageSeparator')}} {{ resourceSecondaryAge.maxAge }} {{ innerI18n.t('teacher.years')}}
          v-popover.item(
            placement='top-start'
            :delay='{show: 100, hide: 600}'
            trigger='hover click'
            popoverClass='krea-actions-popover'
            :container='false'
          )
            .column.item#subjects
              .row.top.start.m-bottom-1
                img.item-logo(src='@/assets/ic_areas.svg' width='20px' alt='resource areas logo')
                .column.width-100
                  h3.m-bottom-05.text-small.dark-gray {{ innerI18n.t('presentationTab.areas') }}
                  .item-info.column.top.start.m-bottom-05(v-if='subjectEdition')
                    krea-multiple-k-select.width-100(
                      small noActions
                      :maxSelected='3'
                      :value='resource.subjectIds'
                      :placeholder='innerI18n.t("krea.resource.presentationTab.subjectsPlaceholder")'
                      name='subjectIds'
                      :options='subjectsOptions'
                      @selectOption='editMultipleSelectResource'
                    )
                  .column(v-if='selectedSubjects.length')
                    p.text-normal(v-for='subject in selectedSubjects')
                      | {{ innerI18n.t(`subjects.${toLowerCamelCase(subject.translationId)}`) }}
            template(slot='popover')
              actions(@edit='toggleSubjectEdition' placement='start' edit :info='subjectsInfo')
          .column.item
            .row.top.start.m-bottom-1
              img.item-logo(src='@/assets/ic_calendar.svg' alt='resource duration logo')
              .column
                h3.m-bottom-05.text-small.dark-gray {{ innerI18n.t('presentationTab.duration') }}
                p.text-normal {{ innerI18n.tc('presentationTab.session', activeLocalizedResource.classPlans.length) }}
          v-popover.item(
            placement='top-start'
            :delay='{show: 100, hide: 600}'
            trigger='hover click'
            popoverClass='krea-actions-popover'
            :container='false'
          )
            .column
              .row.top.start.m-bottom-1.width-100
                img.item-logo(width='20px' height='20px' src='@/assets/ic_wifi.svg' alt='resource mode logo')
                .column.width-100
                  h3.m-bottom-05.text-small.dark-gray {{ innerI18n.t('presentationTab.mode') }}
                  .item-info.column.top.start.m-bottom-05(v-if='implementationTypeEdition')
                    krea-k-select.width-100(
                      small noActions
                      :placeholder='innerI18n.t("presentationTab.mode")'
                      :value='resource.implementationType'
                      name='implementationType'
                      :options='implementationTypeOptions'
                      @selectOption='editResourceImplementationType'
                    )
                  .column(v-else)
                    h2.text-normal {{ getImplementationType(resource.implementationType) }}
            template(slot='popover')
              actions(@edit='toggleImplementationTypeEdition' placement='start' edit :info='implementationInfo')
          v-popover.item(
            placement='top-start'
            :delay='{show: 100, hide: 600}'
            trigger='hover click'
            popoverClass='krea-actions-popover'
            :container='false'
          )
            .column#skills
              .row.top.start.m-bottom-1
                img.item-logo(src='@/assets/ic_check_square.svg' alt='resource skills logo')
                .column
                  h3.m-bottom-05.text-small.dark-gray {{ innerI18n.t('presentationTab.skills') }}
                  .item-info.column.top.start.m-bottom-05(v-if='skillEdition')
                    krea-multiple-k-select.width-100(
                      small noActions
                      :maxSelected='3'
                      :value='resource.skillIds'
                      name='skillIds'
                      :options='skillsOptions'
                      :placeholder='innerI18n.t("krea.resource.presentationTab.skillsPlaceholder")'
                      @selectOption='editMultipleSelectResource'
                    )
                  .column(v-if='selectedSkills.length')
                    p.text-normal(v-for='skill in selectedSkills')
                      | {{ innerI18n.t(`presentationTab.skillsNames.${toLowerCamelCase(skill.translationId)}`) }}
            template(slot='popover')
              actions(@edit='toggleSkillEdition' placement='start' edit :info='skillsInfo')
        .item-1.row.m-bottom-2.width-100
          v-popover.width-100(
            placement='top-start'
            :delay='{show: 100, hide: 600}'
            trigger='hover click'
            popoverClass='krea-actions-popover'
            :container='false'
          )
            .column.width-100#odSs
              .row.top.start.ods-title-container.m-bottom-2
                img.item-logo(src='@/assets/SDG Wheel_Transparent_WEB.png' alt='sdg\'s wheel logo' width='24px')
                .column.item-1
                  h3.text-big.black {{ innerI18n.t('presentationTab.ods') }}
                  .width-100.m-top-1(v-if='odsEdition')
                    krea-multiple-k-select.width-100(
                      small noActions
                      :placeholder='innerI18n.t("krea.resource.presentationTab.sdgPlaceholder")'
                      name='odsIds'
                      :value='resource.odsIds'
                      @selectOption='editMultipleSelectResource'
                      :options='odsOptions'
                    )
              .sdg-grid.width-100
                span(v-for='ods in selectedOds.map(ods => ods).sort((a,b)=> a.id - b.id)')
                  img.sdg-card(:src='getOdsLogo(ods.translationId)' :alt='ods.translationId')
            template(slot='popover')
              actions(@edit='toggleOdsEdition' placement='start' edit :info='odsInfo')
        .item-1.m-bottom-2.width-100.custom-fields.column(v-if='customKreaFields && customKreaFields.length')
          krea-custom-field(
            v-for='field of customKreaFields'
            :key='field.id'
            :field='field'
            edit
          )
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { innerI18n } from '@/main'
import { toLowerCamelCase } from '@/utils/string'
import { isImage, getExtension } from '@/utils/file'
import Actions from '@/components/Krea/Inputs/Actions/Actions'
import AuthorBioTooltip from '@/components/AuthorBioTooltip'
import KreaTextArea from '@/components/Krea/Inputs/KreaTextArea'
import KreaFileUploader from '@/components/Krea/Inputs/KreaFileUploader'
import KreaKSelect from '@/components/Krea/Inputs/KreaKSelect'
import KreaMultipleKSelect from '@/components/Krea/Inputs/KreaMultipleKSelect'
import defaultImage from '@/assets/empty_profile.svg'
import arrowRight from '@/assets/ic_chevron-right.svg'
import { languagesCodesToString } from '../../../constants/languages'
import { classMaterialsTypes, contentType, resourceMediaTypes } from '../../../utils/constants'
import { routes } from '../../../router'
import { resourceImplementationTypesOptions } from '@/constants/resource'
import electronicMaterialIcon from '@/assets/ic_material_electronic.svg'
import physicalMaterialIcon from '@/assets/ic_material_class.svg'
import downloadableMaterialIcon from '@/assets/ic_download.svg'
import { getOEmbedHtml, getOEmbedUrl } from '../../../utils/embed'
import kreaResourceRedirection from '@/mixins/kreaResourceRedirection'
import { kreaCustomFieldTypes } from '@/constants/kreaCustom'
import KreaCustomField from '../Inputs/KreaCustomField'

export default {
  mixins: [kreaResourceRedirection],
  components: {
    Actions,
    AuthorBioTooltip,
    KreaTextArea,
    KreaFileUploader,
    KreaKSelect,
    KreaMultipleKSelect,
    KreaCustomField
  },
  data() {
    return {
      downloadableMaterialIcon,
      physicalMaterialIcon,
      electronicMaterialIcon,
      routes,
      defaultImage,
      arrowRight,
      innerI18n,
      odsEdition: false,
      skillEdition: false,
      subjectEdition: false,
      implementationTypeEdition: false,
      toLowerCamelCase,
      presentation: false,
      mediaPresentationHtml: null
    }
  },
  async mounted() {
    if (!this.resource.subjectIds.length) {
      this.subjectEdition = true
    }
    if (!this.resource.skillIds.length) {
      this.skillEdition = true
    }
    if (!this.resource.implementationType) {
      this.implementationTypeEdition = true
    }
    if (!this.resource.odsIds.length) {
      this.odsEdition = true
    }
    if (!this.mediaIsNotVideo) {
      const oembedUrl = getOEmbedUrl(this.activeLocalizedResource.mediaPresentation)
      const html = await getOEmbedHtml(oembedUrl)
      this.mediaPresentationHtml = html
    }
  },
  watch: {
    async activeLocalizedResource() {
      if (!this.mediaIsNotVideo) {
        const oembedUrl = getOEmbedUrl(this.activeLocalizedResource.mediaPresentation)
        const html = await getOEmbedHtml(oembedUrl)
        this.mediaPresentationHtml = html
      }
    }
  },
  computed: {
    ...mapState({
      resource: state => state.kreaResource.resource,
      activeLanguage: state => state.kreaResource.activeLanguage,
      ods: state => state.dashboard.ods,
      skills: state => state.dashboard.skills,
      subjects: state => state.profile.subjects,
      ages: state => state.dashboard.ages,
      language: state => state.behavior.locale,
      innerLocale: state => state.behavior.innerLocale,
      customFields: state => state.userInfo.customFields
    }),
    customKreaFields() {
      return this.customFields && this.customFields.customizedKreaFields
        .filter(field => field.type === kreaCustomFieldTypes.resource && field.location === 'presentationTab')
    },
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    getContentType() {
      const contentTypeName = Object.keys(contentType).find(key => this.resource.contentType === contentType[key])
      return innerI18n.t(`krea.educationalContent.${contentTypeName}`)
    },
    hasMaterials() {
      if (!this.activeLocalizedResource.classPlans.length) return false
      else return this.activeLocalizedResource.classPlans.some(resClass => resClass.necessaryMaterials.length)
    },
    hasElectronicMaterials() {
      return this.activeLocalizedResource.classPlans.some(resClass => resClass.necessaryMaterials.filter(mat => mat.materialType === classMaterialsTypes.electronic).length)
    },
    hasPhysicalMaterials() {
      return this.activeLocalizedResource.classPlans.some(resClass => resClass.necessaryMaterials.filter(mat => mat.materialType === classMaterialsTypes.physic).length)
    },
    hasDownloadableMaterials() {
      return this.activeLocalizedResource.classPlans.some(resClass => resClass.necessaryMaterials.filter(mat => mat.materialType === classMaterialsTypes.downloadable).length)
    },
    mediaIsNotVideo() {
      return this.activeLocalizedResource.mediaPresentationType !== resourceMediaTypes.video || isImage(getExtension(this.activeLocalizedResource.mediaPresentation))
    },
    resourceAge() {
      return this.ages.find(age => age.id === this.resource.ageRangeId)
    },
    resourceSecondaryAge() {
      return this.ages.find(age => age.id === this.resource.secondaryAgeRangeId)
    },
    activeLocalizedResource() {
      return this.resource.localizedResources.find(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    modeValue() {
      return this.resource.online ? 1 : 0
    },
    selectedSubjects() {
      return this.subjects.filter(subject => this.resource.subjectIds.includes(subject.id))
    },
    subjectsOptions() {
      return this.subjects.map(subject => ({
        ...subject, value: subject.id, label: innerI18n.t(`subjects.${toLowerCamelCase(subject.translationId)}`)
      }))
    },
    selectedSkills() {
      return this.skills.filter(skill => this.resource.skillIds.includes(skill.id))
    },
    implementationTypeOptions() {
      return resourceImplementationTypesOptions
        .filter(type => type.value)
        .map(type => ({ ...type, label: innerI18n.t(`presentationTab.implementationType.${type.label}`) }))
    },
    skillsOptions() {
      return this.skills.map(skill => ({
        ...skill, value: skill.id, label: this.innerI18n.t(`skills.${skill.translationId}`)
      }))
    },
    selectedOds() {
      return this.ods.filter(ods => this.resource.odsIds.includes(ods.id))
    },
    odsOptions() {
      return this.ods.map(ods => ({
        ...ods,
        value: ods.id,
        label: `
          <div class="row middle">
            <img width="22px" height="22px" src="${this.getOdsLogo(ods.translationId)}"/>
            <h2 class="text-normal m-left-1 dark-gray">${this.innerI18n.t(`dashboard.searchBar.ods.${ods.translationId}`)}</h2>
          </div>
        `
      }))
    },
    rhetoricalQuestionInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.rhetoricalQuestionInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.rhetoricalQuestionInfo')]
      }
    },
    benefitsInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.benefitsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.benefitsInfo')]
      }
    },
    mediaPresentationInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.mediaPresentationInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.mediaPresentationInfo')]
      }
    },
    odsInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.odsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.odsInfo')]
      }
    },
    skillsInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.skillsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.skillsInfo')]
      }
    },
    subjectsInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.subjectsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.subjectsInfo')]
      }
    },
    implementationInfo() {
      return {
        title: innerI18n.t('krea.resource.presentationTab.implementationInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [innerI18n.t('krea.resource.presentationTab.implementationInfo')]
      }
    }
  },
  methods: {
    ...mapActions([
      'editKreaLocalizedResourceField',
      'editKreaResourceField',
      'showElement',
      'hideElement'
    ]),
    toggleImplementationTypeEdition() {
      if (this.resource.implementationType) {
        this.implementationTypeEdition = !this.implementationTypeEdition
      }
    },
    toggleOdsEdition() {
      if (this.selectedOds.length) {
        this.odsEdition = !this.odsEdition
      }
    },
    toggleSkillEdition() {
      if (this.selectedSkills.length) {
        this.skillEdition = !this.skillEdition
      }
    },
    toggleSubjectEdition() {
      if (this.selectedSubjects.length) {
        this.subjectEdition = !this.subjectEdition
      }
    },
    toTeacherTab() {
      this.$emit('toTeacher')
    },
    getImplementationType(implementation) {
      const label = resourceImplementationTypesOptions.find(type => type.value === implementation).label
      return innerI18n.t(`presentationTab.implementationType.${label}`)
    },
    getAgeRangeStyle(color) {
      return {
        '--age-color': color
      }
    },
    getOdsLogo(ods) {
      return require(`@/assets/ods/${this.innerI18n.locale}/${ods}.png`)
    },
    editResourceImplementationType(field, value) {
      this.editKreaResourceField({ field, value })
    },
    editLocalizedField(value, field) {
      this.editKreaLocalizedResourceField({ field, value, locale: this.activeLanguage })
    },
    editMultipleSelectResource(field, value) {
      this.editKreaResourceField({ field, value })
    },
    editResourcePresentationImage(value) {
      this.editKreaLocalizedResourceField({ field: 'mediaPresentation', value, locale: this.activeLanguage })
      this.editKreaLocalizedResourceField({ field: 'mediaPresentationType', value: resourceMediaTypes.image, locale: this.activeLanguage })
    },
    editResourcePresentationVideo(value) {
      this.editKreaLocalizedResourceField({ field: 'mediaPresentation', value, locale: this.activeLanguage })
      this.editKreaLocalizedResourceField({ field: 'mediaPresentationType', value: resourceMediaTypes.video, locale: this.activeLanguage })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.custom-fields{
  gap: 10px;
}

.sdg-grid{
  padding-left: 32px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 50px);
  gap: 10px;
}

.sdg-card {
  box-shadow: 0 4px 8px 0 $shadow-gray;
  box-sizing: border-box;
  transition: box-shadow 0.3s;
  width: 50px;

  &:hover {
    box-shadow: 0 8px 16px 0 $dark-gray-translusent;
    transform: scale(2);
  }
}

.ods-title-container {
  min-width: 610px;

  .text-xbig {
    font-size: 20px
  }
}

.resource {
  width: 100%;
  margin-bottom: 60px;
}

.info {
  width: 100%;
  margin-right: 130px;
  max-width: 480px;
  padding-top: 20px;
}

.text-overflow{
  overflow-wrap: anywhere;
}

.author {
  max-width: 500px;
  display: grid;
  grid-template: 'image name' 30px
    'image description' / 60px 1fr;
  gap: 0 20px;
}

.author-avatar {
  box-sizing: border-box;
  height: 60px;
  width: 60px;
  border: 1px solid $light-gray;
  border-radius: 100%;
  grid-area: image;
}

.blue-icon {
  top: 10px;
}

.graphics {
  width: 100%;
}

.media-box {
  width: 100%;
  height: 395px;
  min-width: 300px;
  max-width: 645px;
  margin-bottom: 35px;
  &.empty{
    border: 1px solid $light-gray;
  }
  .media-box-uploader{
    width: 430px;
  }
}

.graphics-items {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 35px;
  width: 100%;
  margin-bottom: 45px;
  border-top: 1px solid $gray;
  padding-top: 30px;
}

.item {
  width: 100%;
}

.item-logo {
  margin-right: 10px;
}

.item-info {
  width: 100%;
}

.age-desc {
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    background-color: var(--age-color);
    border-radius: 4px;
    height: 8px;
    position: absolute;
    margin-right: 10px;
    width: 8px;
    left: 0px;
  }
}

.video {
  border: 0;
  height: 100%;
}

.media-picture {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.materials-items {
  margin-bottom: 20px;
  width: 100%;
}

.small-icon {
  height: 20px;
  width: 20px;
}

.author-tooltip {
  width: 100%;
  max-width: 345px;
  background-color: $white;
}

.author-description {
  padding: 30px 25px 0;
}

.author-name {
  margin-bottom: 5px;
}
</style>
